"use client";

import React, { useRef } from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import { useLazyQuery } from "@apollo/client/react";
import { gql } from "@apollo/client";

const cloudName = (process.env.NEXT_PUBLIC_CLOUD_NAME || "").trim();
const apiKey = (process.env.NEXT_PUBLIC_CLOUD_API_KEY || "").replace(/"/g, "");
const cloudinaryReady = !!(cloudName && apiKey);

const WIDGET_URL = "https://widget.cloudinary.com/v2.0/global/all.js";
let loadingPromise: Promise<any> | null = null;

function ensureCloudinaryWidget(): Promise<any> {
  if (typeof window !== "undefined" && (window as any).cloudinary) {
    return Promise.resolve((window as any).cloudinary);
  }
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve, reject) => {
    const exists = Array.from(document.getElementsByTagName("script")).some(
      (s) => s.src === WIDGET_URL,
    );
    if (exists) {
      const poll = () => {
        if ((window as any).cloudinary) return resolve((window as any).cloudinary);
        setTimeout(poll, 50);
      };
      poll();
      return;
    }
    const script = document.createElement("script");
    script.src = WIDGET_URL;
    script.async = true;
    script.onload = () => {
      if ((window as any).cloudinary) resolve((window as any).cloudinary);
      else reject(new Error("Cloudinary loaded but global missing"));
    };
    script.onerror = () => reject(new Error("Failed to load Cloudinary widget"));
    document.head.appendChild(script);
  });

  return loadingPromise;
}

const CLOUDINARY_SIGNATURE = gql`
  query (
    $timestamp: Int!
    $purpose: CloudinaryUploadPurpose!
    $signupToken: String
  ) {
    cloudinaryGenerateSignature(
      timestamp: $timestamp
      purpose: $purpose
      signupToken: $signupToken
    ) {
      signature
      folder
      publicId
      folderMode
    }
  }
`;

// Shape of the `cloudinaryGenerateSignature` query above (server type
// `cloudinarySignature`: every field is a nullable String). There is no
// GraphQL codegen in public-site, so Apollo cannot infer these from the
// document; without them `data` is typed `{}` and the read below fails
// `next build` with TS2339.
interface CloudinarySignatureData {
  cloudinaryGenerateSignature: {
    signature: string | null;
    folder: string | null;
    publicId: string | null;
    folderMode: string | null;
  } | null;
}

interface CloudinarySignatureVars {
  timestamp: number;
  purpose: "SIGNUP_PROFILE";
  signupToken: string;
}

/**
 * Public-site Cloudinary uploader — currently used only for the signup
 * profile photo, which uploads before the account exists (no session yet).
 * Uses purpose: SIGNUP_PROFILE, scoped server-side to `signupToken` instead
 * of a userId. See CloudinaryUploadService.js (server) for the rationale.
 *
 * Reverted Aug 21 2026 to the pre-fetch-signature pattern (signature
 * requested before the widget opens, not during upload via
 * prepareUploadParams) — the widget's public API does not appear to expose
 * raw file bytes to custom code during upload, which made content-hash-based
 * publicId generation unworkable. This matches the original, proven-working
 * pattern used elsewhere in the app.
 *
 * Props:
 * - signupToken: string (required) — a per-signup-session random token,
 *   e.g. generated once via crypto.randomUUID() when the signup form mounts.
 * - onUpload?: (url: string) => void
 * - buttonTitle?: string
 */
export default function CloudinaryUploadWidget({
  signupToken,
  onUpload,
  buttonTitle = "Upload profile photo",
}: {
  signupToken: string;
  onUpload?: (url: string) => void;
  buttonTitle?: string;
}) {
  const widgetRef = useRef<any>(null);
  const [getSignature] = useLazyQuery<CloudinarySignatureData, CloudinarySignatureVars>(CLOUDINARY_SIGNATURE, {
    fetchPolicy: "no-cache",
  });

  const handleClick = async () => {
    try {
      if (!cloudinaryReady) return;
      if (!signupToken) {
        console.error("CloudinaryUploadWidget: signupToken is required");
        return;
      }

      const cld = await ensureCloudinaryWidget();
      const ts = Math.round(Date.now() / 1000);

      const { data } = await getSignature({
        variables: { timestamp: ts, purpose: "SIGNUP_PROFILE", signupToken },
      });
      const result = data?.cloudinaryGenerateSignature;
      if (!result?.signature) throw new Error("Missing Cloudinary signature");

      widgetRef.current = cld.createUploadWidget(
        {
          cloudName,
          apiKey,
          uploadSignatureTimestamp: ts,
          uploadSignature: result.signature,
          publicId: result.publicId,
          overwrite: true,
          ...(result.folderMode === "dynamic"
            ? { assetFolder: result.folder }
            : { folder: result.folder }),
          multiple: false,
          sources: ["local", "url", "camera"],
          clientAllowedFormats: ["jpg", "jpeg", "png", "webp"],
          maxFileSize: 10_000_000,
        },
        (err: any, result: any) => {
          if (err) return;
          if (result?.event === "success") {
            const secureUrl = result.info?.secure_url;
            if (secureUrl) onUpload?.(secureUrl);
          }
        },
      );

      widgetRef.current?.open();
    } catch (e) {
      console.error("Cloudinary uploader error:", e);
    }
  };

  return (
    <Tooltip title={buttonTitle} placement="right">
      <IconButton onClick={handleClick} size="large" sx={{ m: 1 }} disabled={!cloudinaryReady}>
        <InsertPhotoIcon fontSize="medium" />
      </IconButton>
    </Tooltip>
  );
}
